import {
  CheckCircle2,
  Crown,
  LoaderCircle,
  LogIn,
  Mail,
  ShieldCheck,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const apiUrl = process.env.REACT_APP_BACKEND_URL;

export default function PilotClaimPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [authState, setAuthState] = useState("loading");
  const [email, setEmail] = useState("");
  const [isClaiming, setIsClaiming] = useState(false);
  const [result, setResult] = useState(null);
  const token = new URLSearchParams(location.search).get("token") || "";
  const returnTo = `${location.pathname}${location.search}`;
  const loginHref = `/login?returnTo=${encodeURIComponent(returnTo)}`;

  const checkSession = useCallback(async () => {
    try {
      const response = await fetch(`${apiUrl}/api/auth/me`, {
        credentials: "include",
      });
      if (!response.ok) {
        setAuthState("signed-out");
        return;
      }
      const user = await response.json();
      setEmail(user.email);
      const passResponse = await fetch(`${apiUrl}/api/pilot/status`, {
        credentials: "include",
      });
      if (passResponse.ok) {
        const pass = await passResponse.json();
        if (pass.has_pending_pass) {
          setResult({ type: "pending" });
        } else if (pass.is_pro && pass.expires_at) {
          setResult({ type: "active", expiresAt: pass.expires_at });
        }
      }
      setAuthState("ready");
    } catch (error) {
      setAuthState("signed-out");
    }
  }, []);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  const claimPass = async () => {
    setIsClaiming(true);
    setResult(null);
    try {
      const response = await fetch(`${apiUrl}/api/pilot/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token }),
      });
      const body = await response.json();
      if (!response.ok) {
        const message =
          typeof body.detail === "string"
            ? body.detail
            : "This invitation could not be claimed.";
        throw new Error(message);
      }
      setResult({ type: "pending" });
    } catch (error) {
      setResult({ type: "error", message: error.message });
    } finally {
      setIsClaiming(false);
    }
  };

  const activatePass = async () => {
    setIsClaiming(true);
    setResult(null);
    try {
      const response = await fetch(`${apiUrl}/api/pilot/activate`, {
        method: "POST",
        credentials: "include",
      });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.detail || "Your Tournament Pass could not be started.");
      }
      setResult({ type: "active", expiresAt: body.expires_at });
    } catch (error) {
      setResult({ type: "error", message: error.message });
    } finally {
      setIsClaiming(false);
    }
  };

  const formattedExpiry = result?.expiresAt
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(result.expiresAt))
    : "";

  return (
    <main className="pilot-claim-page" data-testid="pilot-claim-page">
      <section className="pilot-claim-panel">
        <p className="eyebrow">
          <ShieldCheck size={14} /> Private invitation
        </p>
        <h1>Your tournament pass.</h1>
        {!token && !result && authState !== "loading" && (
          <p
            className="pilot-claim-message error"
            data-testid="pilot-claim-missing-token"
          >
            This invitation link is incomplete. Please use the link in your email.
          </p>
        )}
        {token && authState === "loading" && (
          <div
            className="pilot-claim-loading"
            data-testid="pilot-claim-auth-loading"
          >
            <LoaderCircle className="payment-spinner" size={24} />
            Checking your invitation
          </div>
        )}
        {token && authState === "signed-out" && (
          <div className="pilot-claim-copy" data-testid="pilot-claim-sign-in-required">
            <p>
              Sign in or create an account with the email address that received this
              invitation before claiming your 72-hour Tournament Pilot Pass.
            </p>
            <Link
              className="primary-action"
              to={loginHref}
              data-testid="pilot-claim-sign-in-link"
            >
              <LogIn size={17} />
              Sign in to claim
            </Link>
          </div>
        )}
        {token && authState === "ready" && !result && (
          <div
            className="pilot-claim-copy"
            data-testid="pilot-claim-ready-state"
          >
            <p>
              This pass is tied to{" "}
              <strong data-testid="pilot-claim-account-email">{email}</strong>.
              Claiming secures your pass. You can start the 72-hour Pro window
              later when your tournament begins.
            </p>
            <button
              className="pilot-pass-button"
              onClick={claimPass}
              disabled={isClaiming}
              data-testid="pilot-claim-submit-button"
            >
              {isClaiming ? (
                <LoaderCircle className="payment-spinner" size={18} />
              ) : (
                <Crown size={18} />
              )}
              {isClaiming ? "Claiming pass" : "Claim Tournament Pass"}
            </button>
          </div>
        )}
        {result?.type === "pending" && (
          <div
            className="pilot-claim-message success"
            data-testid="pilot-pass-pending"
          >
            <CheckCircle2 size={24} />
            <div>
              <strong>Pass claimed.</strong>
              <span>Your 72-hour Pro window has not started yet.</span>
            </div>
            <button
              className="primary-action"
              onClick={activatePass}
              disabled={isClaiming}
              data-testid="pilot-start-pass-button"
            >
              {isClaiming ? "Starting pass" : "Start Tournament Pass"}
            </button>
          </div>
        )}
        {result?.type === "active" && (
          <div
            className="pilot-claim-message success"
            data-testid="pilot-pass-active"
          >
            <CheckCircle2 size={24} />
            <div>
              <strong>Tournament Pass started.</strong>
              <span data-testid="pilot-claim-expiry">
                Pro access ends {formattedExpiry}.
              </span>
            </div>
            <button
              className="primary-action"
              onClick={() => navigate("/")}
              data-testid="pilot-claim-workspace-button"
            >
              Open workspace
            </button>
          </div>
        )}
        {result?.type === "error" && (
          <div
            className="pilot-claim-message error"
            data-testid="pilot-claim-error"
          >
            {result.message}
          </div>
        )}
        <a
          className="pilot-claim-support"
          href="mailto:support@pfjudgepro.com"
          data-testid="pilot-claim-support-link"
        >
          <Mail size={15} />
          Need help with your invitation?
        </a>
      </section>
    </main>
  );
}